import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NavBar } from '../components/NavBar';
import './SupplierDetail.css';

const PIPELINE_STAGES = ['Identified', 'Intro', 'Vetting', 'Pilot', 'Active'];
const SPECIAL_STAGES = ['At Risk', 'Dormant', 'Offboarded'];
const VETTING_SUBSTATUS = ['Awaiting Info', 'Testing', 'Negotiating Terms', 'Reference Check'];
const SUPPLIER_TYPES = ['API', 'Manual', 'API + Manual', 'Panel Build', 'Specialty'];
const GEO_OPTIONS = ['US', 'UK', 'Canada', 'Australia', 'EU', 'APAC', 'LATAM', 'MENA', 'Global'];
const SAMPLE_TYPES = ['B2C General Population', 'B2B Decision Makers', 'Healthcare Professionals', 'Hard-to-Reach/Low Incidence', 'Youth/Students', 'Specialty/Niche', 'Multicultural'];
const FRAUD_TOOLS = ['Digital Fingerprinting', 'RelevantID', 'Imperium', 'Research Defender', 'Verisoul', 'IP/Geo Checks', 'Behavioral Analysis', 'Proprietary System', 'Other'];
const ACTIVITY_TYPES = ['Call', 'Check-in', 'Email', 'Note', 'QBR', 'Quality Review', 'Other'];
const CONTACT_TYPES = ['Primary', 'Technical', 'Billing', 'Other'];
const ALLOWED_EXTENSIONS = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv';

export function SupplierDetail() {
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Activity state
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [activityForm, setActivityForm] = useState({
    type: 'Call',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    nextActionDate: ''
  });
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityError, setActivityError] = useState('');

  // Document state
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Contact state
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactForm, setContactForm] = useState({ contactType: 'Primary', name: '', email: '', phone: '', role: '', notes: '' });
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState('');

  const { id } = useParams();
  const { getToken, user: authUser } = useAuth();
  const navigate = useNavigate();

  const fetchSupplier = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/suppliers/${id}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch supplier');
      const data = await response.json();
      setSupplier(data.supplier);
      setFormData(data.supplier);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, getToken]);

  const fetchActivities = useCallback(async () => {
    setActivitiesLoading(true);
    try {
      const response = await fetch(`/api/suppliers/${id}/activities`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities);
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setActivitiesLoading(false);
    }
  }, [id, getToken]);

  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const response = await fetch(`/api/suppliers/${id}/contacts`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setContactsLoading(false);
    }
  }, [id, getToken]);

  const fetchDocuments = useCallback(async () => {
    setDocsLoading(true);
    try {
      const response = await fetch(`/api/suppliers/${id}/documents`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setDocsLoading(false);
    }
  }, [id, getToken]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, [getToken]);

  useEffect(() => {
    fetchSupplier();
    fetchUsers();
  }, [fetchSupplier, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'activity') fetchActivities();
    if (activeTab === 'documents') fetchDocuments();
    if (activeTab === 'contacts') fetchContacts();
  }, [activeTab, fetchActivities, fetchDocuments, fetchContacts]);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`/api/suppliers/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Failed to update supplier');
      await fetchSupplier();
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleStageChange(newStage) {
    if (newStage === supplier.status) return;
    try {
      await fetch(`/api/suppliers/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...supplier, status: newStage })
      });
      await fetchSupplier();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleHealthChange(score) {
    try {
      await fetch(`/api/suppliers/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...supplier, healthScore: supplier.healthScore === score ? '' : score })
      });
      await fetchSupplier();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleVettingSubStatus(sub) {
    try {
      await fetch(`/api/suppliers/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...supplier, vettingSubStatus: supplier.vettingSubStatus === sub ? '' : sub })
      });
      await fetchSupplier();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleLogActivity(e) {
    e.preventDefault();
    setActivityError('');
    if (!activityForm.notes.trim()) {
      setActivityError('Please add notes for this activity');
      return;
    }
    setActivitySaving(true);
    try {
      const response = await fetch(`/api/suppliers/${id}/activities`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(activityForm)
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to log activity');
      }
      setActivityForm({ type: 'Call', date: new Date().toISOString().split('T')[0], notes: '', nextActionDate: '' });
      setShowActivityForm(false);
      await fetchActivities();
      await fetchSupplier();
    } catch (err) {
      setActivityError(err.message);
    } finally {
      setActivitySaving(false);
    }
  }

  async function handleDeleteActivity(activityId) {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await fetch(`/api/suppliers/${id}/activities/${activityId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      await fetchActivities();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('name', file.name);
      const response = await fetch(`/api/suppliers/${id}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: form
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Upload failed');
      }
      await fetchDocuments();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleDownload(doc) {
    try {
      const response = await fetch(`/api/suppliers/${id}/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to get download link');
      const data = await response.json();
      window.open(data.downloadUrl, '_blank');
    } catch (err) {
      alert('Could not download file: ' + err.message);
    }
  }

  async function handleDeleteDoc(docId) {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    try {
      await fetch(`/api/suppliers/${id}/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      await fetchDocuments();
    } catch (err) {
      alert('Could not delete document: ' + err.message);
    }
  }

  async function handleSaveContact(e) {
    e.preventDefault();
    setContactError('');
    if (!contactForm.name.trim() || !contactForm.email.trim()) {
      setContactError('Name and email are required');
      return;
    }
    setContactSaving(true);
    try {
      const url = editingContactId
        ? `/api/suppliers/${id}/contacts/${editingContactId}`
        : `/api/suppliers/${id}/contacts`;
      const response = await fetch(url, {
        method: editingContactId ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save contact');
      }
      setContactForm({ contactType: 'Primary', name: '', email: '', phone: '', role: '', notes: '' });
      setEditingContactId(null);
      setShowContactForm(false);
      await fetchContacts();
    } catch (err) {
      setContactError(err.message);
    } finally {
      setContactSaving(false);
    }
  }

  async function handleDeleteContact(contactId) {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await fetch(`/api/suppliers/${id}/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      await fetchContacts();
    } catch (err) {
      setContactError(err.message);
    }
  }

  function handleStartEditContact(contact) {
    setEditingContactId(contact.id);
    setContactForm({
      contactType: contact.contactType || 'Other',
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      role: contact.role || '',
      notes: contact.notes || ''
    });
    setContactError('');
    setShowContactForm(true);
  }

  function handleCancelContact() {
    setShowContactForm(false);
    setEditingContactId(null);
    setContactForm({ contactType: 'Primary', name: '', email: '', phone: '', role: '', notes: '' });
    setContactError('');
  }

  async function handleDeleteSupplier() {
    if (!window.confirm(`Delete "${supplier.name}"?\n\nThis will permanently remove the supplier and all associated activities, contacts, and documents. This cannot be undone.`)) return;
    try {
      const response = await fetch(`/api/suppliers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to delete supplier');
      navigate('/suppliers');
    } catch (err) {
      setError(err.message);
    }
  }

  function handleCancel() { setFormData(supplier); setEditing(false); setError(''); }
  function handleChange(field, value) { setFormData({ ...formData, [field]: value }); }
  function handleArrayToggle(field, value) {
    const current = formData[field] || [];
    setFormData({ ...formData, [field]: current.includes(value) ? current.filter(i => i !== value) : [...current, value] });
  }

  function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function formatFileSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function getDocIcon(fileName) {
    const ext = (fileName || '').toLowerCase().split('.').pop();
    const icons = {
      pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
      ppt: '📑', pptx: '📑', jpg: '🖼', jpeg: '🖼', png: '🖼',
      gif: '🖼', webp: '🖼', txt: '📃', csv: '📊'
    };
    return icons[ext] || '📎';
  }

  function getStageClass(stage) {
    const map = {
      'Identified': 'stage-identified', 'Intro': 'stage-intro', 'Vetting': 'stage-vetting',
      'Pilot': 'stage-pilot', 'Active': 'stage-active', 'At Risk': 'stage-at-risk',
      'Dormant': 'stage-dormant', 'Offboarded': 'stage-offboarded'
    };
    return map[stage] || 'stage-default';
  }

  function getHealthLabel(score) {
    if (score === 'Green') return { icon: '🟢', label: 'Healthy' };
    if (score === 'Yellow') return { icon: '🟡', label: 'Watch' };
    if (score === 'Red') return { icon: '🔴', label: 'At Risk' };
    return { icon: '⚪', label: 'Unrated' };
  }

  function getActivityIcon(type) {
    const icons = { 'Call': '📞', 'Email': '✉️', 'QBR': '📊', 'Quality Review': '🔍', 'Check-in': '✅', 'Note': '📝', 'Other': '💬' };
    return icons[type] || '💬';
  }

  function isOverdue(d) { return d && new Date(d) < new Date(); }
  function isDueSoon(d) {
    if (!d) return false;
    const due = new Date(d);
    return due > new Date() && due < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  }

  if (loading) return (
    <div className="page-container">
      <NavBar />
      <div className="supplier-detail-container"><div className="loading">Loading supplier...</div></div>
    </div>
  );
  if (error && !supplier) return (
    <div className="page-container">
      <NavBar />
      <div className="supplier-detail-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/suppliers')}>← Back to Suppliers</button>
      </div>
    </div>
  );

  const isSpecialStage = SPECIAL_STAGES.includes(supplier.status);
  const currentStageIdx = PIPELINE_STAGES.indexOf(supplier.status);
  const needsNextAction = ['Check-in', 'QBR', 'Quality Review'].includes(activityForm.type);

  function getDefaultNextAction(type) {
    const today = new Date();
    const addDays = (d) => {
      const date = new Date(today);
      date.setDate(date.getDate() + d);
      return date.toISOString().split('T')[0];
    };
    if (type === 'Check-in') return addDays(30);
    if (type === 'QBR') return addDays(90);
    if (type === 'Quality Review') return addDays(60);
    return '';
  }

  return (
    <div className="page-container">
      <NavBar />
      <div className="supplier-detail-container">
      {/* Header */}
      <div className="supplier-detail-header">
        <button className="back-button" onClick={() => navigate('/suppliers')}>← Back to Suppliers</button>
        <div className="header-actions">
          {editing ? (
            <>
              <button className="button-secondary" onClick={handleCancel}>Cancel</button>
              <button className="button-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              {authUser?.role === 'Admin' && (
                <button className="button-danger" onClick={handleDeleteSupplier}>Delete Supplier</button>
              )}
              <button className="button-primary" onClick={() => setEditing(true)}>Edit Supplier</button>
            </>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Title row */}
      <div className="supplier-title-row">
        <div>
          <h2 className="supplier-name-heading">{supplier.name}</h2>
          <div className="supplier-meta">
            {supplier.supplierType && <span>{supplier.supplierType}</span>}
            {supplier.hqLocation && <span>📍 {supplier.hqLocation}</span>}
            {supplier.accountManager && <span>👤 {supplier.accountManager}</span>}
            {supplier.nextActionDate && (
              <span className={isOverdue(supplier.nextActionDate) ? 'next-action-overdue' : isDueSoon(supplier.nextActionDate) ? 'next-action-soon' : 'next-action'}>
                🗓 Next: {formatDate(supplier.nextActionDate)}
              </span>
            )}
          </div>
        </div>
        <div className="supplier-health-selector">
          <span className="health-label">Health:</span>
          {['Green', 'Yellow', 'Red'].map(score => (
            <button
              key={score}
              className={`health-btn ${supplier.healthScore === score ? 'health-active' : ''}`}
              onClick={() => handleHealthChange(score)}
              title={getHealthLabel(score).label}
            >
              {getHealthLabel(score).icon}
            </button>
          ))}
        </div>
      </div>

      {/* Pipeline bar */}
      <div className="pipeline-section">
        <div className="pipeline-bar">
          {PIPELINE_STAGES.map((stage, idx) => {
            const isPast = !isSpecialStage && idx < currentStageIdx;
            const isCurrent = supplier.status === stage;
            const isFuture = !isSpecialStage && idx > currentStageIdx;
            return (
              <React.Fragment key={stage}>
                <button
                  className={`pipeline-step ${isCurrent ? 'current ' + getStageClass(stage) : ''} ${isPast ? 'past' : ''} ${isFuture ? 'future' : ''}`}
                  onClick={() => handleStageChange(stage)}
                  title={`Move to ${stage}`}
                >
                  <span className="step-number">{idx + 1}</span>
                  <span className="step-name">{stage}</span>
                </button>
                {idx < PIPELINE_STAGES.length - 1 && (
                  <div className={`pipeline-connector ${isPast || isCurrent ? 'filled' : ''}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="special-stages">
          {SPECIAL_STAGES.map(stage => (
            <button
              key={stage}
              className={`special-stage-btn ${getStageClass(stage)} ${supplier.status === stage ? 'active' : ''}`}
              onClick={() => handleStageChange(stage)}
            >
              {stage}
            </button>
          ))}
        </div>
        {supplier.status === 'Vetting' && (
          <div className="vetting-substatus-row">
            <span className="vetting-label">Phase:</span>
            {VETTING_SUBSTATUS.map(sub => (
              <button
                key={sub}
                className={`vetting-sub-btn ${supplier.vettingSubStatus === sub ? 'active' : ''}`}
                onClick={() => handleVettingSubStatus(sub)}
              >
                {sub}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        {['overview', 'capabilities', 'commercial', 'contacts', 'activity', 'documents'].map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'activity' ? `Activity${activities.length > 0 ? ` (${activities.length})` : ''}`
              : tab === 'documents' ? `Documents${documents.length > 0 ? ` (${documents.length})` : ''}`
              : tab === 'contacts' ? `Contacts${contacts.length > 0 ? ` (${contacts.length})` : ''}`
              : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="detail-content">

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            <div className="detail-section">
              <h3>General Information</h3>
              <div className="detail-grid">
                <Field label="Company Name" editing={editing}
                  display={supplier.name}
                  input={<input type="text" value={formData.name || ''} onChange={e => handleChange('name', e.target.value)} />}
                />
                <Field label="Supplier Type" editing={editing}
                  display={supplier.supplierType || '—'}
                  input={
                    <select value={formData.supplierType || ''} onChange={e => handleChange('supplierType', e.target.value)}>
                      <option value="">Select type...</option>
                      {SUPPLIER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  }
                />
                <Field label="Account Manager" editing={editing}
                  display={supplier.accountManager || '—'}
                  input={
                    <select value={formData.accountManager || ''} onChange={e => handleChange('accountManager', e.target.value)}>
                      <option value="">Unassigned</option>
                      {users.map(u => <option key={u.email} value={u.name}>{u.name}</option>)}
                    </select>
                  }
                />
                <Field label="HQ Location" editing={editing}
                  display={supplier.hqLocation || '—'}
                  input={<input type="text" value={formData.hqLocation || ''} onChange={e => handleChange('hqLocation', e.target.value)} />}
                />
                <Field label="Timezone" editing={editing}
                  display={supplier.timezone || '—'}
                  input={<input type="text" value={formData.timezone || ''} onChange={e => handleChange('timezone', e.target.value)} placeholder="e.g. UTC+8" />}
                />
                <Field label="Year Founded" editing={editing}
                  display={supplier.yearFounded || '—'}
                  input={<input type="number" value={formData.yearFounded || ''} onChange={e => handleChange('yearFounded', e.target.value)} />}
                />
                <Field label="Website" editing={editing}
                  display={supplier.companyWebsite ? <a href={supplier.companyWebsite} target="_blank" rel="noopener noreferrer">{supplier.companyWebsite}</a> : '—'}
                  input={<input type="url" value={formData.companyWebsite || ''} onChange={e => handleChange('companyWebsite', e.target.value)} placeholder="https://" />}
                />
                <Field label="Onboarding Date" editing={editing}
                  display={formatDate(supplier.onboardingDate)}
                  input={<input type="date" value={formData.onboardingDate || ''} onChange={e => handleChange('onboardingDate', e.target.value)} />}
                />
              </div>
              <div className="detail-field full-width">
                <label>Company Description</label>
                {editing ? (
                  <textarea value={formData.companyDescription || ''} onChange={e => handleChange('companyDescription', e.target.value)} rows="3" placeholder="Brief description..." />
                ) : (
                  <div className="field-value">{supplier.companyDescription || '—'}</div>
                )}
              </div>
              <div className="detail-field full-width">
                <label>Notes</label>
                {editing ? (
                  <textarea value={formData.notes || ''} onChange={e => handleChange('notes', e.target.value)} rows="4" placeholder="Internal notes..." />
                ) : (
                  <div className="field-value notes-value">{supplier.notes || '—'}</div>
                )}
              </div>
            </div>

            {(supplier.lastActivityDate || supplier.nextActionDate) && (
              <div className="detail-section activity-summary">
                <h3>Activity Summary</h3>
                <div className="detail-grid">
                  <div className="detail-field">
                    <label>Last Activity</label>
                    <div className="field-value">
                      {supplier.lastActivityDate
                        ? `${formatDate(supplier.lastActivityDate)}${supplier.lastActivityType ? ` · ${supplier.lastActivityType}` : ''}`
                        : '—'}
                    </div>
                  </div>
                  <div className="detail-field">
                    <label>Next Action</label>
                    <div className={`field-value ${isOverdue(supplier.nextActionDate) ? 'overdue-text' : isDueSoon(supplier.nextActionDate) ? 'soon-text' : ''}`}>
                      {formatDate(supplier.nextActionDate)}
                    </div>
                  </div>
                </div>
                <button className="link-btn" onClick={() => setActiveTab('activity')}>View full activity log →</button>
              </div>
            )}
          </>
        )}

        {/* CAPABILITIES TAB */}
        {activeTab === 'capabilities' && (
          <div className="detail-section">
            <h3>Capabilities & Coverage</h3>
            <div className="detail-grid">
              <Field label="Panel Size" editing={editing}
                display={supplier.panelSize || '—'}
                input={<input type="text" value={formData.panelSize || ''} onChange={e => handleChange('panelSize', e.target.value)} placeholder="e.g. 500K" />}
              />
              <Field label="Support Hours" editing={editing}
                display={supplier.supportHours || '—'}
                input={<input type="text" value={formData.supportHours || ''} onChange={e => handleChange('supportHours', e.target.value)} placeholder="e.g. 24/7" />}
              />
              <Field label="Integration Timeline" editing={editing}
                display={supplier.integrationTimeline || '—'}
                input={<input type="text" value={formData.integrationTimeline || ''} onChange={e => handleChange('integrationTimeline', e.target.value)} placeholder="e.g. 2-4 weeks" />}
              />
              <Field label="Certifications" editing={editing}
                display={supplier.certifications || '—'}
                input={<input type="text" value={formData.certifications || ''} onChange={e => handleChange('certifications', e.target.value)} placeholder="e.g. ISO 20252" />}
              />
            </div>
            <div className="detail-field full-width">
              <label>Geographic Coverage</label>
              {editing ? (
                <div className="checkbox-grid">
                  {GEO_OPTIONS.map(geo => (
                    <label key={geo} className="checkbox-item">
                      <input type="checkbox" checked={(formData.geoCoverage || []).includes(geo)} onChange={() => handleArrayToggle('geoCoverage', geo)} />
                      {geo}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="tag-list">
                  {(supplier.geoCoverage || []).length > 0 ? supplier.geoCoverage.map(g => <span key={g} className="tag">{g}</span>) : '—'}
                </div>
              )}
            </div>
            <div className="detail-field full-width">
              <label>Sample Types</label>
              {editing ? (
                <div className="checkbox-grid">
                  {SAMPLE_TYPES.map(t => (
                    <label key={t} className="checkbox-item">
                      <input type="checkbox" checked={(formData.sampleTypes || []).includes(t)} onChange={() => handleArrayToggle('sampleTypes', t)} />
                      {t}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="tag-list">
                  {(supplier.sampleTypes || []).length > 0 ? supplier.sampleTypes.map(t => <span key={t} className="tag">{t}</span>) : '—'}
                </div>
              )}
            </div>
            <div className="detail-field full-width">
              <label>Fraud / Quality Tools</label>
              {editing ? (
                <div className="checkbox-grid">
                  {FRAUD_TOOLS.map(t => (
                    <label key={t} className="checkbox-item">
                      <input type="checkbox" checked={(formData.fraudTools || []).includes(t)} onChange={() => handleArrayToggle('fraudTools', t)} />
                      {t}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="tag-list">
                  {(supplier.fraudTools || []).length > 0 ? supplier.fraudTools.map(t => <span key={t} className="tag">{t}</span>) : '—'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* COMMERCIAL TAB */}
        {activeTab === 'commercial' && (
          <div className="detail-section">
            <h3>Commercial Terms</h3>
            <div className="detail-grid">
              <Field label="Quality Rating" editing={editing}
                display={<span className={`quality-badge quality-${supplier.qualityRating?.toLowerCase() || 'none'}`}>{supplier.qualityRating || 'Not rated'}</span>}
                input={
                  <select value={formData.qualityRating || ''} onChange={e => handleChange('qualityRating', e.target.value)}>
                    <option value="">Not rated</option>
                    <option value="A">A — Excellent</option>
                    <option value="B">B — Good</option>
                    <option value="C">C — Acceptable</option>
                    <option value="D">D — Poor</option>
                  </select>
                }
              />
              <Field label="Payment Terms" editing={editing}
                display={supplier.paymentTerms || '—'}
                input={<input type="text" value={formData.paymentTerms || ''} onChange={e => handleChange('paymentTerms', e.target.value)} placeholder="e.g. Net 30" />}
              />
              <Field label="Preferred Currency" editing={editing}
                display={supplier.preferredCurrency || '—'}
                input={
                  <select value={formData.preferredCurrency || 'USD'} onChange={e => handleChange('preferredCurrency', e.target.value)}>
                    {['USD', 'GBP', 'EUR', 'AUD', 'CAD', 'SGD', 'INR'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                }
              />
              <Field label="Primary Contact" editing={editing}
                display={supplier.primaryContactName || '—'}
                input={<input type="text" value={formData.primaryContactName || ''} onChange={e => handleChange('primaryContactName', e.target.value)} />}
              />
              <Field label="Contact Email" editing={editing}
                display={supplier.primaryContactEmail ? <a href={`mailto:${supplier.primaryContactEmail}`}>{supplier.primaryContactEmail}</a> : '—'}
                input={<input type="email" value={formData.primaryContactEmail || ''} onChange={e => handleChange('primaryContactEmail', e.target.value)} />}
              />
              <Field label="Contact Phone" editing={editing}
                display={supplier.primaryContactPhone || '—'}
                input={<input type="tel" value={formData.primaryContactPhone || ''} onChange={e => handleChange('primaryContactPhone', e.target.value)} />}
              />
            </div>
          </div>
        )}

        {/* ACTIVITY TAB */}
        {activeTab === 'activity' && (
          <div className="detail-section">
            <div className="activity-header">
              <h3>Activity Log</h3>
              <button className="button-primary" onClick={() => setShowActivityForm(!showActivityForm)}>
                {showActivityForm ? 'Cancel' : '+ Log Activity'}
              </button>
            </div>

            {showActivityForm && (
              <form onSubmit={handleLogActivity} className="activity-form">
                <div className="activity-form-row">
                  <div className="form-field">
                    <label>Type</label>
                    <select value={activityForm.type} onChange={e => {
                    const newType = e.target.value;
                    const defaultDate = getDefaultNextAction(newType);
                    setActivityForm({ ...activityForm, type: newType, nextActionDate: defaultDate });
                  }}>
                      {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Date</label>
                    <input type="date" value={activityForm.date} onChange={e => {
                    const newDate = e.target.value;
                    const defaultOffset = { 'Check-in': 30, 'QBR': 90, 'Quality Review': 60 };
                    const days = defaultOffset[activityForm.type];
                    const nextAction = days && newDate ? (() => {
                      const d = new Date(newDate);
                      d.setDate(d.getDate() + days);
                      return d.toISOString().split('T')[0];
                    })() : activityForm.nextActionDate;
                    setActivityForm({ ...activityForm, date: newDate, nextActionDate: nextAction });
                  }} />
                  </div>
                  {needsNextAction && (
                    <div className="form-field">
                      <label>Next Action Date <span className="optional">optional</span></label>
                      <input type="date" value={activityForm.nextActionDate} onChange={e => setActivityForm({ ...activityForm, nextActionDate: e.target.value })} />
                    </div>
                  )}
                </div>
                <div className="form-field">
                  <label>Notes *</label>
                  <textarea
                    value={activityForm.notes}
                    onChange={e => setActivityForm({ ...activityForm, notes: e.target.value })}
                    placeholder="What happened? Key discussion points, outcomes, action items..."
                    rows="3"
                  />
                </div>
                {activityError && <div className="form-error">{activityError}</div>}
                <div className="activity-form-actions">
                  <button type="submit" className="button-primary" disabled={activitySaving}>
                    {activitySaving ? 'Saving...' : 'Log Activity'}
                  </button>
                </div>
              </form>
            )}

            {activitiesLoading ? (
              <div className="loading">Loading activities...</div>
            ) : activities.length === 0 ? (
              <div className="empty-activities">
                <p>No activities logged yet.</p>
                <button className="button-primary" onClick={() => setShowActivityForm(true)}>Log First Activity</button>
              </div>
            ) : (
              <div className="activity-timeline">
                {activities.map(activity => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon">{getActivityIcon(activity.type)}</div>
                    <div className="activity-body">
                      <div className="activity-meta">
                        <span className="activity-type">{activity.type}</span>
                        <span className="activity-date">{formatDate(activity.date)}</span>
                        <span className="activity-logged-by">by {activity.loggedByName}</span>
                        {activity.nextActionDate && (
                          <span className={`activity-next-action ${isOverdue(activity.nextActionDate) ? 'overdue-text' : ''}`}>
                            → Next: {formatDate(activity.nextActionDate)}
                          </span>
                        )}
                      </div>
                      <div className="activity-notes">{activity.notes}</div>
                    </div>
                    <button className="activity-delete" onClick={() => handleDeleteActivity(activity.id)} title="Delete activity">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CONTACTS TAB */}
        {activeTab === 'contacts' && (
          <div className="detail-section">
            <div className="activity-header">
              <h3>Contacts</h3>
              {!showContactForm && (
                <button className="button-primary" onClick={() => {
                  setEditingContactId(null);
                  setContactForm({ contactType: 'Primary', name: '', email: '', phone: '', role: '', notes: '' });
                  setContactError('');
                  setShowContactForm(true);
                }}>+ Add Contact</button>
              )}
            </div>

            {showContactForm && (
              <form onSubmit={handleSaveContact} className="activity-form">
                <div className="activity-form-row">
                  <div className="form-field">
                    <label>Type</label>
                    <select value={contactForm.contactType} onChange={e => setContactForm({ ...contactForm, contactType: e.target.value })}>
                      {CONTACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Name *</label>
                    <input type="text" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Full name" />
                  </div>
                  <div className="form-field">
                    <label>Email *</label>
                    <input type="email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} placeholder="email@company.com" />
                  </div>
                </div>
                <div className="activity-form-row">
                  <div className="form-field">
                    <label>Phone <span className="optional">optional</span></label>
                    <input type="tel" value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="+1 555 000 0000" />
                  </div>
                  <div className="form-field">
                    <label>Role / Title <span className="optional">optional</span></label>
                    <input type="text" value={contactForm.role} onChange={e => setContactForm({ ...contactForm, role: e.target.value })} placeholder="e.g. VP Partnerships" />
                  </div>
                </div>
                <div className="form-field">
                  <label>Notes <span className="optional">optional</span></label>
                  <textarea
                    value={contactForm.notes}
                    onChange={e => setContactForm({ ...contactForm, notes: e.target.value })}
                    placeholder="Any relevant notes about this contact..."
                    rows="2"
                  />
                </div>
                {contactError && <div className="form-error">{contactError}</div>}
                <div className="activity-form-actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" className="button-secondary" onClick={handleCancelContact}>Cancel</button>
                  <button type="submit" className="button-primary" disabled={contactSaving}>
                    {contactSaving ? 'Saving...' : editingContactId ? 'Save Changes' : 'Add Contact'}
                  </button>
                </div>
              </form>
            )}

            {contactsLoading ? (
              <div className="loading">Loading contacts...</div>
            ) : contacts.length === 0 && !showContactForm ? (
              <div className="empty-activities">
                <p>No contacts added yet.</p>
                <button className="button-primary" onClick={() => setShowContactForm(true)}>Add First Contact</button>
              </div>
            ) : (
              <div className="contacts-list">
                {contacts.map(contact => (
                  <div key={contact.id} className="contact-item">
                    <div className="contact-main">
                      <div className="contact-header-row">
                        <span className={`contact-type-badge contact-type-${contact.contactType?.toLowerCase()}`}>{contact.contactType}</span>
                        <span className="contact-name">{contact.name}</span>
                        {contact.role && <span className="contact-role">{contact.role}</span>}
                      </div>
                      <div className="contact-details">
                        {contact.email && <a href={`mailto:${contact.email}`} className="contact-email">✉ {contact.email}</a>}
                        {contact.phone && <span className="contact-phone">📞 {contact.phone}</span>}
                      </div>
                      {contact.notes && <div className="contact-notes">{contact.notes}</div>}
                    </div>
                    <div className="contact-actions">
                      <button className="doc-action-btn" onClick={() => handleStartEditContact(contact)} title="Edit">✎</button>
                      <button className="doc-action-btn doc-delete-btn" onClick={() => handleDeleteContact(contact.id)} title="Delete">×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* DOCUMENTS TAB */}
        {activeTab === 'documents' && (
          <div className="detail-section">
            <div className="activity-header">
              <h3>Documents</h3>
              <label className={`button-primary upload-label ${uploading ? 'disabled' : ''}`}>
                {uploading ? 'Uploading...' : '+ Upload Document'}
                <input
                  type="file"
                  style={{ display: 'none' }}
                  accept={ALLOWED_EXTENSIONS}
                  onChange={handleUpload}
                  disabled={uploading}
                />
              </label>
            </div>

            {uploadError && <div className="form-error" style={{ marginBottom: 16 }}>{uploadError}</div>}

            {docsLoading ? (
              <div className="loading">Loading documents...</div>
            ) : documents.length === 0 ? (
              <div className="empty-activities">
                <p>No documents uploaded yet.</p>
                <label className="button-primary upload-label">
                  Upload First Document
                  <input
                    type="file"
                    style={{ display: 'none' }}
                    accept={ALLOWED_EXTENSIONS}
                    onChange={handleUpload}
                    disabled={uploading}
                  />
                </label>
              </div>
            ) : (
              <div className="documents-list">
                {documents.map(doc => (
                  <div key={doc.id} className="document-item">
                    <div className="document-icon">{getDocIcon(doc.fileName)}</div>
                    <div className="document-body">
                      <div className="document-name">{doc.displayName}</div>
                      <div className="document-meta">
                        {formatFileSize(doc.sizeBytes)} · {formatDate(doc.uploadedAt)} · {doc.uploadedByName}
                      </div>
                    </div>
                    <div className="document-actions">
                      <button className="doc-action-btn" onClick={() => handleDownload(doc)} title="Download">⬇</button>
                      {authUser?.role === 'Admin' && (
                        <button className="doc-action-btn doc-delete-btn" onClick={() => handleDeleteDoc(doc.id)} title="Delete">×</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      <div className="detail-metadata">
        <span>Created {formatDate(supplier.createdAt)} by {supplier.createdBy}</span>
        <span>Updated {formatDate(supplier.updatedAt)} by {supplier.updatedBy}</span>
      </div>
    </div>
    </div>
  );
}

function Field({ label, editing, display, input }) {
  return (
    <div className="detail-field">
      <label>{label}</label>
      {editing ? input : <div className="field-value">{display}</div>}
    </div>
  );
}
